import { z } from 'zod'

import { walletSchema } from '@/features/veluvit/schemas/wallet.schema'

const object = z.record(z.string(), z.unknown())
export const importLineSchema = z.object({
  who: z.enum(['char', 'user', 'narration']),
  text: z.string(),
  characterName: z.string().optional(),
})
const importStartSchema = z.object({
  name: z.string().optional(),
  place: z.string().optional(),
  intro: z
    .object({
      context: z.string().optional(),
      lines: z.array(importLineSchema).optional(),
    })
    .optional(),
  suggestions: z.array(z.string()).optional(),
})
const importBundleSchema = z.object({
  work: object.nullable(),
  world: object.nullable(),
  status: object.nullable(),
  characters: z.array(object),
  commands: z.array(object),
  starts: z.array(importStartSchema),
})
const importMatchSchema = z.object({
  id: z.string().min(1),
  revision: z.string().min(1),
  status: z.enum(['exact', 'similar', 'conflict']),
  confidence: z.string(),
  basis: z.array(z.string()),
  differences: z.array(z.string()),
  data: object,
  contentFingerprint: z.string(),
  fieldDifferences: z.record(z.string(), z.array(z.string())).optional(),
  fieldOrderChanged: z.boolean().optional(),
})
export const importAnalysisSchema = z.looseObject({
  schema: z.literal('studio-bundle'),
  applied: z.literal(false),
  bundle: importBundleSchema,
  candidates: z.array(
    z.object({
      candidate: z.string(),
      kind: z.enum(['world', 'character', 'status', 'command']),
      data: object,
      matches: z.array(importMatchSchema),
      errors: z.array(z.string()),
      recommendation: z.enum(['reuse', 'review', 'create']),
    })
  ),
  warnings: z.array(z.string()),
  unclassified: z.array(z.unknown()),
  wallet: walletSchema.optional(),
})
export const importConfirmedSchema = z.looseObject({
  applied: z.literal(true),
  analysisId: z.string(),
  atomic: z.literal(true),
  materials: z.array(
    z.object({
      candidate: z.string(),
      action: z.enum(['create', 'reuse', 'update']),
      kind: z.enum(['world', 'character', 'status', 'command']),
      material: z.looseObject({ id: z.string(), revision: z.string() }),
      contentFingerprint: z.string(),
    })
  ),
  work: z
    .looseObject({ id: z.string(), revision: z.string(), work: object })
    .nullable(),
  wallet: walletSchema.optional(),
})
export type ImportBundle = z.infer<typeof importBundleSchema>
export type ImportAnalysis = z.infer<typeof importAnalysisSchema>
export type ImportConfirmed = z.infer<typeof importConfirmedSchema>
